
import { useState, useRef, useCallback } from 'react';
import { Subscription } from './types';

export function useSubscriptionState() {
  // Subscription status state
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Refs for managing async operations and cleanup
  const checkingTimeoutRef = useRef<number | null>(null);
  const isRefreshingRef = useRef(false);
  const isMountedRef = useRef(true);
  
  // Clear subscription state - needed when user cancels a subscription
  const clearSubscriptionState = useCallback(() => {
    setCurrentSubscription(null);
    setIsSubscribed(false);
    setLastChecked(null);
    
    // Clear any pending timeouts
    if (checkingTimeoutRef.current) {
      window.clearTimeout(checkingTimeoutRef.current);
      checkingTimeoutRef.current = null;
    }
  }, []);
  
  // Cleanup function for unmounting
  const setupCleanup = useCallback(() => {
    return () => {
      isMountedRef.current = false;
      // Clear any pending timeouts
      if (checkingTimeoutRef.current) {
        window.clearTimeout(checkingTimeoutRef.current);
        checkingTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    // State getters
    currentSubscription,
    isSubscribed,
    isLoading,
    isRetrying,
    lastChecked,
    
    // State setters
    setCurrentSubscription,
    setIsSubscribed, 
    setIsLoading,
    setIsRetrying,
    setLastChecked,
    
    // Refs
    checkingTimeoutRef,
    isRefreshingRef,
    isMountedRef,
    
    // Utilities
    setupCleanup,
    clearSubscriptionState
  };
}
